import {
  type LngLat,
  type SuggestResponse as SuggestResponseBase,
  useAddNotification,
  useTransition,
} from '@logistics-frontend/blocks'
import {DEFAULT_CENTER} from '@logistics-frontend/blocks/map/consts'
import type {ServerError} from '@logistics-frontend/core/utils/network'
import {get, getErrorMessage, pipe} from '@logistics-frontend/utils'
import {useFormContext} from '@yandex-delivery/form'
import {type Point, SuggestAction} from '@yandex-taxi/js-integration-api'
import {useCallback} from 'react'

import {fetchAddressSuggest} from 'src/shared/api'
import type {SuggestObject, SuggestResponse} from 'src/shared/types'
import {fromSuggestToPointAddress} from 'src/shared/utils'

import {getAddressCoordinatesFieldName} from './utils/get-address-coordinates-field-name'
import {getAddressFieldName} from './utils/get-address-field-name'

export function useFetchAddressByLocation(location: LngLat = DEFAULT_CENTER) {
  const {setFieldValue} = useFormContext()
  const addNotification = useAddNotification()
  const [{isPending, payload: suggestValue}, startTransition] = useTransition<
    SuggestResponseBase<SuggestResponse, SuggestObject>,
    ServerError
  >(undefined, {
    key: 'fetch-taxi-suggest',
  })

  const fetchTaxiSuggestHandler = useCallback(
    (coordinates: LngLat, index: number) => {
      const fieldCoordinatesPath = getAddressCoordinatesFieldName(index)
      setFieldValue(fieldCoordinatesPath, coordinates)

      startTransition(
        () =>
          fetchAddressSuggest({
            location: location as Point,
            action: SuggestAction.PIN_DROP,
            point: coordinates as Point,
            type: index === 0 ? 'a' : 'b',
          }),
        {
          onCommit: result => {
            const address = pipe(result, get('suggest[0].__origin'), fromSuggestToPointAddress)

            const fieldPath = getAddressFieldName(index)
            setFieldValue(fieldPath, address)
          },
          onError: error => {
            addNotification(getErrorMessage(error), {
              type: 'error',
            })
            setFieldValue(fieldCoordinatesPath, undefined)
          },
        },
      )
    },
    [addNotification, location, setFieldValue],
  )

  return {
    isPending,
    suggestValue,
    fetchTaxiSuggestHandler,
  }
}
